<template>
  <div class="bike-setup">
    <InlineSvg
      :src="getBikeImage"
      @loaded="transform"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { BikeCategory } from '@/types/bike'
import { useI18n } from 'vue-i18n'

import InlineSvg from 'vue-inline-svg'

import BikeCity from '@/assets/bike-city.svg'
import BikeCyclocross from '@/assets/bike-cyclocross.svg'
import BikeFullTriathlon from '@/assets/bike-full-triathlon.svg'
import BikeGravelElectric from '@/assets/bike-gravel-electric.svg'
import BikeGravel from '@/assets/bike-gravel.svg'
import BikeMtbCrossCountry from '@/assets/bike-mtb-cross-country.svg'
import BikeMtbElectric from '@/assets/bike-mtb-electric.svg'
import BikeMtbTrail from '@/assets/bike-mtb-trail.svg'
import BikeRoadElectric from '@/assets/bike-road-electric.svg'
import BikeRoad from '@/assets/bike-road.svg'
import BikeShortTriathlon from '@/assets/bike-short-triathlon.svg'
import BikeTimeTrial from '@/assets/bike-time-trial.svg'
import BikeTrekkingElectric from '@/assets/bike-trekking-electric.svg'
import BikeTrekking from '@/assets/bike-trekking.svg'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    bikeCategory?: BikeCategory
    hideDelta?: boolean
    hideStackReach?: boolean
    reach?: number
    stack?: number
    saddleWidth?: number
    saddleHeight?: number
    saddleHeightDiff?: number
    saddleToHandlebar?: number
    saddleToHandlebarDiff?: number
    saddleHandlebarDrop?: number
    saddleHandlebarDropDiff?: number
    pedalHeight?: number
    pedalHeightDiff?: number
    crankArmLength?: number
    crankArmLengthDiff?: number
  }>(),
  {
    bikeCategory: BikeCategory.ROAD,
    hideDelta: false,
    hideStackReach: false,
    reach: 1,
    stack: 2,
    saddleWidth: 3,
    saddleHeight: 4,
    saddleHeightDiff: 5,
    saddleToHandlebar: 6,
    saddleToHandlebarDiff: 7,
    saddleHandlebarDrop: 8,
    saddleHandlebarDropDiff: 9,
    pedalHeight: 10,
    pedalHeightDiff: 11,
    crankArmLength: 12,
    crankArmLengthDiff: 13,
  },
)

const getBikeImage = computed(() => {
  switch (props.bikeCategory) {
    case BikeCategory.CITY:
      return BikeCity
    case BikeCategory.CYCLOCROSS:
      return BikeCyclocross
    case BikeCategory.FULLTRIATHLON:
      return BikeFullTriathlon
    case BikeCategory.GRAVEL:
      return BikeGravel
    case BikeCategory.GRAVELELECTRIC:
      return BikeGravelElectric
    case BikeCategory.MTBCROSSCOUNTRY:
      return BikeMtbCrossCountry
    case BikeCategory.MTBELECTRIC:
      return BikeMtbElectric
    case BikeCategory.MTBTRAIL:
      return BikeMtbTrail
    case BikeCategory.ROAD:
      return BikeRoad
    case BikeCategory.ROADELECTRIC:
      return BikeRoadElectric
    case BikeCategory.SHORTTRIATHLON:
      return BikeShortTriathlon
    case BikeCategory.TIMETRIAL:
      return BikeTimeTrial
    case BikeCategory.TREKKING:
      return BikeTrekking
    case BikeCategory.TREKKINGELECTRIC:
      return BikeTrekkingElectric
    default:
      return BikeRoad
  }
})

const transform = (svg: any) => {
  const removeElements = [
    '#SizeDaST',
    '#SizeCaST',
    '#SizeOkST',
    '#SizeDaRE',
    '#SizeCaRE',
    '#SizeOkRE',
    '#SPCRSTLabel',
    '#SPCRSTDelta',
    '#SPCRSTEMSTLabel',
    '#SPCRDelta',
    '#STEMANLabel',
    '#STEMANDelta',
    '#STEMST',
    '#STEMSTDelta',
    '#HAST',
    '#HASTDelta',
    '#CPST',
    '#CPSTDelta',
    '#SSDelta',
    '#SS',
    '#SAHA',
    '#SAHADelta',
    '#STEMRE',
    '#STEMREDelta',
    '#HBRE',
    '#CPRE',
    '#SPCRSTEMST',
    '#CPREDelta',
    '#HBW',
    '#HBTOP',
    '#STEMAN',
    '#SPCRST',
    '#HBTOPExtent',
    '#Extent',
    '#HBAN',
    '#HBANDelta',
    '#HBREDelta',
  ]

  const removeDeltaElements = ['#GraphDelta', '#SAHEDelta', '#SAHBDelta']
  const elementsInMm = [
    '#SizeRecRELabelValue tspan',
    '#SizeRecSTLabelValue tspan',
    '#SADTOPValue tspan',
    '#SAHELabelValue tspan',
    '#SAHBLabelValue tspan',
    '#SAHBDROPLabelValue tspan',
    '#PDSTLabelValue tspan',
    '#CALabelValue tspan',
  ]

  const replaceElements = [
    { '#SizeRecRELabelValue tspan': props.reach },
    { '#SizeRecSTLabelValue tspan': props.stack },
    { '#SADTOPValue tspan': props.saddleWidth },
    { '#SAHELabelValue tspan': props.saddleHeight },
    { '#SAHEDeltaValue tspan': props.saddleHeightDiff },
    { '#SAHBLabelValue tspan': props.saddleToHandlebar },
    { '#SAHBDeltaValue tspan': props.saddleToHandlebarDiff },
    { '#SAHBDROPLabelValue tspan': props.saddleHandlebarDrop },
    { '#SAHBDROPDeltaValue tspan': props.saddleHandlebarDropDiff },
    { '#PDSTLabelValue tspan': props.pedalHeight },
    { '#PDSTDeltaValue tspan': props.pedalHeightDiff },
    { '#CALabelValue tspan': props.crankArmLength },
    { '#CADeltaValue tspan': props.crankArmLengthDiff },
  ]

  removeElements.forEach((element) => {
    svg.querySelector(element)?.remove()
  })

  if (props.hideDelta) {
    removeDeltaElements.forEach((element) => {
      svg.querySelector(element)?.remove()
    })
  }

  if (props.hideStackReach) {
    svg.querySelector('#Sizes')?.remove()
    svg.querySelector('#Stack-Range')?.remove()
    svg.querySelector('#Reach-Range')?.remove()
    svg.querySelector('#Hand-CP-Zone')?.remove()
  }

  replaceElements.forEach((element) => {
    const el = svg.querySelector(Object.keys(element)[0])

    if (el) {
      if (elementsInMm.includes(Object.keys(element)[0])) {
        el.textContent = `${Object.values(element)[0]} ${t('common.unit.mm')}`
      } else {
        switch (Math.sign(Object.values(element)[0])) {
          case 1:
            el.textContent = t('common.unit.plus')
            break

          case -1:
            el.textContent = t('common.unit.minus')
            break

          default:
            el.textContent = ''
        }
        el.textContent += Object.values(element)[0]
      }
    }
  })
}
</script>

<style lang="scss">
.bike-setup {
  width: 100%;
}
</style>
